import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BasicLayout } from '@/components/BasicLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } };
};

// Protected collections that need extra confirmation
const PROTECTED = new Set(['companies', 'super_admins', 'users', 'roles']);

function DatabaseAdmin({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [collections, setCollections] = React.useState([]);
  const [databaseName, setDatabaseName] = React.useState('');
  const [selectedCollection, setSelectedCollection] = React.useState(null);
  const [documents, setDocuments] = React.useState([]);
  const [totalDocs, setTotalDocs] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [loadingDocs, setLoadingDocs] = React.useState(false);

  // Edit/Create modal
  const [editModal, setEditModal] = React.useState(null); // { mode: 'edit'|'create'|'view', doc, json }
  const [editJson, setEditJson] = React.useState('');
  const [jsonError, setJsonError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = React.useState(null); // single doc
  const [deleting, setDeleting] = React.useState(false);

  // Bulk selection
  const [selected, setSelected] = React.useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = React.useState(false);

  // Company-wise filter (Super Admin)
  const [companies, setCompanies] = React.useState([]);
  const [filterCompanyId, setFilterCompanyId] = React.useState('');

  React.useEffect(() => { fetchCollections(); fetchCompanies(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCollections = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/db/collections`, getAuthHeader());
      setCollections(r.data.collections);
      setDatabaseName(r.data.database);
    } catch (err) {
      if (err.response?.status === 403) { toast.error('Super Admin access required'); navigate('/modules'); }
      else toast.error('Failed to fetch database info');
    }
    setLoading(false);
  };

  const fetchCompanies = async () => {
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/db/collections/companies?limit=1000`,
        getAuthHeader()
      );
      setCompanies(r.data.documents || []);
    } catch {
      // Silent — selector simply shows "All Companies"
    }
  };

  const fetchCollectionData = async (name, page = 1, search = '', companyFilter = filterCompanyId) => {
    setLoadingDocs(true);
    try {
      const p = new URLSearchParams({ page, limit: 20 });
      if (search) p.append('search', search);
      if (companyFilter) p.append('filter_company_id', companyFilter);
      const r = await axios.get(`${BACKEND_URL}/api/admin/db/collections/${name}?${p}`, getAuthHeader());
      setDocuments(r.data.documents);
      setTotalDocs(r.data.total);
      setCurrentPage(r.data.page);
      setTotalPages(r.data.total_pages);
    } catch { toast.error('Failed to fetch data'); }
    setLoadingDocs(false);
  };

  const handleCompanyFilterChange = (newId) => {
    setFilterCompanyId(newId);
    setCurrentPage(1);
    setSelected(new Set());
    if (selectedCollection) {
      fetchCollectionData(selectedCollection.name, 1, searchTerm, newId);
    }
  };

  const selectCollection = (coll) => {
    setSelectedCollection(coll);
    setSearchTerm('');
    setCurrentPage(1);
    setSelected(new Set());
    setEditModal(null);
    fetchCollectionData(coll.name, 1, '');
  };

  const handleSearch = (e) => { e.preventDefault(); setCurrentPage(1); fetchCollectionData(selectedCollection.name, 1, searchTerm); };
  const handlePageChange = (p) => { setCurrentPage(p); setSelected(new Set()); fetchCollectionData(selectedCollection.name, p, searchTerm); };

  const refresh = () => {
    fetchCollectionData(selectedCollection.name, currentPage, searchTerm);
    fetchCollections();
    setSelected(new Set());
  };

  // ---- Export ----
  const handleExport = async (fmt) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/db/export/${selectedCollection.name}?format=${fmt}`, { ...getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${selectedCollection.name}.${fmt}`; document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Exported as ${fmt.toUpperCase()}`);
    } catch { toast.error('Export failed'); }
  };

  // ---- Create ----
  const openCreate = () => {
    const template = { id: '', note: 'Fill in your document fields' };
    setEditJson(JSON.stringify(template, null, 2));
    setJsonError('');
    setEditModal({ mode: 'create', doc: null });
  };

  // ---- View ----
  const openView = (doc) => {
    setEditJson(JSON.stringify(doc, null, 2));
    setJsonError('');
    setEditModal({ mode: 'view', doc });
  };

  // ---- Edit ----
  const openEdit = (doc) => {
    setEditJson(JSON.stringify(doc, null, 2));
    setJsonError('');
    setEditModal({ mode: 'edit', doc });
  };

  const handleSave = async () => {
    let parsed;
    try { parsed = JSON.parse(editJson); } catch { setJsonError('Invalid JSON format'); return; }
    setJsonError('');
    setSaving(true);

    try {
      if (editModal.mode === 'create') {
        await axios.post(`${BACKEND_URL}/api/admin/db/collections/${selectedCollection.name}/document`, { document: parsed }, getAuthHeader());
        toast.success('Document created');
      } else {
        const docId = editModal.doc?.id;
        if (!docId) { toast.error('Document has no id field — cannot update'); setSaving(false); return; }
        await axios.put(`${BACKEND_URL}/api/admin/db/collections/${selectedCollection.name}/document/${docId}`, { document: parsed }, getAuthHeader());
        toast.success('Document updated');
      }
      setEditModal(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  // ---- Delete single ----
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/db/collections/${selectedCollection.name}/document/${deleteTarget.id}`, getAuthHeader());
      toast.success('Document deleted');
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
    setDeleting(false);
  };

  // ---- Bulk delete ----
  const confirmBulkDelete = async () => {
    setDeleting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/db/collections/${selectedCollection.name}/bulk-delete`, { ids: [...selected] }, getAuthHeader());
      toast.success(`${selected.size} documents deleted`);
      setBulkDeleteOpen(false);
      setSelected(new Set());
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    }
    setDeleting(false);
  };

  // ---- Selection helpers ----
  const toggleSelect = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };
  const toggleAll = () => {
    const ids = documents.filter(d => d.id).map(d => d.id);
    if (ids.every(id => selected.has(id))) setSelected(new Set());
    else setSelected(new Set(ids));
  };

  // ---- Table columns ----
  const getColumns = () => {
    if (!documents.length) return [];
    const keys = Object.keys(documents[0]).filter(k => k !== '_id');
    return keys.slice(0, 6);
  };

  const fmtVal = (v) => {
    if (v === null || v === undefined) return <span className="text-slate-400 italic">null</span>;
    if (typeof v === 'boolean') return <span className={`font-medium ${v ? 'text-emerald-600' : 'text-red-500'}`}>{v.toString()}</span>;
    if (typeof v === 'object') return <span className="text-violet-600 font-mono text-xs">{JSON.stringify(v).substring(0, 40)}...</span>;
    const s = String(v);
    return s.length > 45 ? s.substring(0, 45) + '...' : s;
  };

  const isProtected = selectedCollection && PROTECTED.has(selectedCollection.name);

  if (loading) return (
    <BasicLayout user={user} onLogout={onLogout}>
      <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" /></div>
    </BasicLayout>
  );

  return (
    <BasicLayout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 data-testid="db-admin-title" className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
              Database Console
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">
              <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs">{databaseName}</span>
              <span className="ml-2 text-slate-400">{collections.length} collections</span>
            </p>
          </div>
          <button onClick={() => navigate('/modules')} className="px-4 py-2 text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm">
            Back to Modules
          </button>
        </div>

        {/* View Company selector (Super Admin) */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <label htmlFor="db-admin-company-filter" className="text-sm font-medium text-slate-700 whitespace-nowrap">
            View Company:
          </label>
          <select
            id="db-admin-company-filter"
            data-testid="db-admin-company-filter"
            value={filterCompanyId}
            onChange={(e) => handleCompanyFilterChange(e.target.value)}
            className="w-full sm:w-72 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id || c._id} value={c.id}>
                {c.company_name || c.name || c.id}
              </option>
            ))}
          </select>
          {filterCompanyId && (
            <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              Scoped to one company
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                <h2 className="font-semibold text-slate-700 text-sm">Collections</h2>
              </div>
              <div className="divide-y divide-slate-50 max-h-[70vh] overflow-y-auto">
                {collections.map(c => (
                  <button key={c.name} data-testid={`coll-${c.name}`} onClick={() => selectCollection(c)}
                    className={`w-full px-3 py-2.5 text-left hover:bg-slate-50 transition-colors flex items-center justify-between text-sm ${
                      selectedCollection?.name === c.name ? 'bg-emerald-50 border-l-3 border-emerald-500' : ''
                    }`}>
                    <span className={`font-medium truncate ${selectedCollection?.name === c.name ? 'text-emerald-700' : 'text-slate-700'}`}>
                      {c.name}
                      {PROTECTED.has(c.name) && <span className="ml-1 text-amber-500 text-xs" title="Protected">&#9888;</span>}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
                      selectedCollection?.name === c.name ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>{c.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-4">
            {!selectedCollection ? (
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
                <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="text-lg font-medium text-slate-500">Select a Collection</h3>
                <p className="text-slate-400 text-sm mt-1">Choose from the sidebar to browse, edit, or manage documents</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                {/* Toolbar */}
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <h2 className="font-semibold text-slate-800 text-lg flex items-center gap-2">
                        {selectedCollection.name}
                        {isProtected && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-normal">Protected</span>}
                      </h2>
                      <p className="text-xs text-slate-500">{totalDocs} documents</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button data-testid="add-document-btn" onClick={openCreate}
                        className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        Add Document
                      </button>
                      {selected.size > 0 && (
                        <button data-testid="bulk-delete-btn" onClick={() => setBulkDeleteOpen(true)}
                          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          Delete ({selected.size})
                        </button>
                      )}
                      <button onClick={() => handleExport('json')} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 border border-blue-200">JSON</button>
                      <button onClick={() => handleExport('csv')} className="px-3 py-1.5 text-sm bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 border border-emerald-200">CSV</button>
                    </div>
                  </div>

                  {/* Search */}
                  <form onSubmit={handleSearch} className="flex gap-2">
                    <input data-testid="db-search-input" type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by name, email, mobile, ID..."
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm bg-white" />
                    <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm">Search</button>
                    {searchTerm && <button type="button" onClick={() => { setSearchTerm(''); fetchCollectionData(selectedCollection.name, 1, ''); }}
                      className="px-3 py-2 text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm">Clear</button>}
                  </form>
                </div>

                {/* Table */}
                {loadingDocs ? (
                  <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-12"><p className="text-slate-400">No documents found</p></div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="documents-table">
                        <thead className="bg-slate-50 border-b border-slate-100">
                          <tr>
                            <th className="pl-4 pr-1 py-3 w-10">
                              <input type="checkbox" checked={documents.filter(d => d.id).length > 0 && documents.filter(d => d.id).every(d => selected.has(d.id))}
                                onChange={toggleAll} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                            </th>
                            {getColumns().map(k => (
                              <th key={k} className="px-3 py-3 text-left font-medium text-slate-500 whitespace-nowrap text-xs uppercase tracking-wider">{k}</th>
                            ))}
                            <th className="px-3 py-3 text-right font-medium text-slate-500 text-xs uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {documents.map((doc, i) => (
                            <tr key={doc.id || i} className={`hover:bg-slate-50 transition-colors ${selected.has(doc.id) ? 'bg-emerald-50/50' : ''}`}>
                              <td className="pl-4 pr-1 py-2.5">
                                {doc.id ? (
                                  <input type="checkbox" checked={selected.has(doc.id)} onChange={() => toggleSelect(doc.id)}
                                    className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                                ) : <span className="text-slate-300">-</span>}
                              </td>
                              {getColumns().map(k => (
                                <td key={k} className="px-3 py-2.5 text-slate-700 max-w-[200px] truncate">{fmtVal(doc[k])}</td>
                              ))}
                              <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1">
                                  <button data-testid={`view-btn-${i}`} onClick={() => openView(doc)} title="View"
                                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                  </button>
                                  <button data-testid={`edit-btn-${i}`} onClick={() => openEdit(doc)} title="Edit"
                                    className="p-1.5 rounded hover:bg-blue-50 text-blue-500 hover:text-blue-700">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                  </button>
                                  {doc.id && (
                                    <button data-testid={`delete-btn-${i}`} onClick={() => setDeleteTarget(doc)} title="Delete"
                                      className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
                        <p className="text-sm text-slate-500">Page {currentPage} of {totalPages} &middot; {totalDocs} total</p>
                        <div className="flex gap-2">
                          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1}
                            className="px-3 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
                          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages}
                            className="px-3 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ========== EDIT / CREATE / VIEW MODAL ========== */}
        {editModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditModal(null)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="px-5 py-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between rounded-t-xl">
                <div>
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    {editModal.mode === 'create' && <>
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                      New Document
                    </>}
                    {editModal.mode === 'edit' && <>
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      Edit Document
                    </>}
                    {editModal.mode === 'view' && <>
                      <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      View Document
                    </>}
                  </h3>
                  {editModal.doc?.id && <p className="text-xs text-slate-500 font-mono mt-0.5">ID: {editModal.doc.id}</p>}
                  <p className="text-xs text-slate-400 mt-0.5">Collection: {selectedCollection?.name}</p>
                </div>
                <button data-testid="close-modal-btn" onClick={() => setEditModal(null)} className="p-2 hover:bg-slate-200 rounded-lg">
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* JSON Editor */}
              <div className="flex-1 overflow-y-auto p-5">
                {editModal.mode === 'view' ? (
                  <pre className="text-sm bg-slate-900 text-emerald-400 p-4 rounded-lg overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap">
                    {editJson}
                  </pre>
                ) : (
                  <>
                    {isProtected && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                        <span><strong>Protected collection.</strong> Changes to <code>{selectedCollection?.name}</code> can break the application. Edit carefully.</span>
                      </div>
                    )}
                    <textarea data-testid="json-editor" value={editJson} onChange={e => { setEditJson(e.target.value); setJsonError(''); }}
                      className={`w-full h-[50vh] font-mono text-sm p-4 rounded-lg border-2 bg-slate-900 text-emerald-400 focus:outline-none focus:ring-2 resize-none leading-relaxed ${
                        jsonError ? 'border-red-500 focus:ring-red-400' : 'border-slate-700 focus:ring-emerald-500'
                      }`}
                      spellCheck={false} />
                    {jsonError && <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      {jsonError}
                    </p>}
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between gap-2 rounded-b-xl bg-slate-50">
                <div>
                  {editModal.mode !== 'view' && (
                    <button onClick={() => { try { setEditJson(JSON.stringify(JSON.parse(editJson), null, 2)); setJsonError(''); toast.success('Formatted'); } catch { setJsonError('Invalid JSON — cannot format'); } }}
                      className="px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-100">
                      Format JSON
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { navigator.clipboard.writeText(editJson); toast.success('Copied'); }}
                    className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">Copy</button>
                  {editModal.mode === 'view' ? (
                    <button onClick={() => { setEditModal({ ...editModal, mode: 'edit' }); }}
                      data-testid="switch-to-edit-btn"
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                      Edit
                    </button>
                  ) : (
                    <button data-testid="save-document-btn" onClick={handleSave} disabled={saving}
                      className="px-5 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50 flex items-center gap-2">
                      {saving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                      {editModal.mode === 'create' ? 'Create' : 'Save Changes'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== DELETE CONFIRMATION ========== */}
        {deleteTarget && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDeleteTarget(null)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 text-lg">Delete Document</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Are you sure you want to permanently delete this document from <strong>{selectedCollection?.name}</strong>?
                  </p>
                  <p className="text-xs font-mono text-slate-400 mt-2 bg-slate-50 px-2 py-1 rounded">ID: {deleteTarget.id}</p>
                  {isProtected && (
                    <p className="text-sm text-amber-700 mt-2 bg-amber-50 px-3 py-2 rounded border border-amber-200">
                      This is a protected collection. Deletion may break the application.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button data-testid="cancel-delete-btn" onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button data-testid="confirm-delete-btn" onClick={confirmDelete} disabled={deleting}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                  {deleting && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========== BULK DELETE CONFIRMATION ========== */}
        {bulkDeleteOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setBulkDeleteOpen(false)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 text-lg">Bulk Delete</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    You are about to permanently delete <strong className="text-red-600">{selected.size} documents</strong> from <strong>{selectedCollection?.name}</strong>.
                  </p>
                  {isProtected && (
                    <p className="text-sm text-amber-700 mt-2 bg-amber-50 px-3 py-2 rounded border border-amber-200">
                      This is a protected collection. Bulk deletion may break the application.
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-2">This action cannot be undone.</p>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button onClick={() => setBulkDeleteOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button data-testid="confirm-bulk-delete-btn" onClick={confirmBulkDelete} disabled={deleting}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                  {deleting && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                  Delete {selected.size} Documents
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </BasicLayout>
  );
}

export default DatabaseAdmin;
